/**
 * Generated by orval v8.5.3 🍺
 * Do not edit manually.
 * Api
 * KL Project API — Multi-branch KTV management platform
 * OpenAPI spec version: 0.2.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  AddOrderItemRequest,
  ApiError,
  ApiSuccess,
  ApplyDiscountRequest,
  BranchDashboard,
  BranchListResponse,
  BranchResponse,
  CancelReservationRequest,
  CreateBranchRequest,
  CreateOrderRequest,
  CreateProductGroupRequest,
  CreateProductRequest,
  CreateProductTypeRequest,
  CreateReceiptRequest,
  CreateReservationRequest,
  CreateRoomRequest,
  ExtendReservationRequest,
  GetOrderInvoiceParams,
  GetReceiptParams,
  GetReservationAvailabilityParams,
  HealthStatus,
  ListAvailableDriversParams,
  ListAvailableHostessesParams,
  ListAvailableRoomsParams,
  ListOrdersParams,
  ListProductTypesParams,
  ListProductsParams,
  ListReservationsParams,
  ListRoomsParams,
  LoginRequest,
  LoginResponse,
  OrderListResponse,
  OrderResponse,
  ProductGroupListResponse,
  ProductGroupResponse,
  ProductListResponse,
  ProductResponse,
  ProductTypeListResponse,
  ProductTypeResponse,
  ReceiptResponse,
  RefreshRequest,
  RefreshResponse,
  ReservationListResponse,
  ReservationResponse,
  RoomBoardResponse,
  RoomListResponse,
  RoomResponse,
  StaffAvailabilityResponse,
  UpdateBranchRequest,
  UpdateProductRequest,
  UpdateRoomRequest,
  UpdateRoomStatusRequest,
  UserProfile,
} from "./api.schemas";

import { customFetch } from "../custom-fetch";
import type { ErrorType, BodyType } from "../custom-fetch";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * Returns server health status and DB connectivity
 * @summary Health check
 */
export const getHealthCheckUrl = () => {
  return `/api/healthz`;
};

export const healthCheck = async (
  options?: RequestInit,
): Promise<HealthStatus> => {
  return customFetch<HealthStatus>(getHealthCheckUrl(), {
    ...options,
    method: "GET",
  });
};

export const getHealthCheckQueryKey = () => {
  return [`/api/healthz`] as const;
};

export const getHealthCheckQueryOptions = <
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getHealthCheckQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof healthCheck>>> = ({
    signal,
  }) => healthCheck({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type HealthCheckQueryResult = NonNullable<
  Awaited<ReturnType<typeof healthCheck>>
>;
export type HealthCheckQueryError = ErrorType<unknown>;

/**
 * @summary Health check
 */

export function useHealthCheck<
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getHealthCheckQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Login
 */
export const getLoginUrl = () => {
  return `/api/auth/login`;
};

export const login = async (
  loginRequest: LoginRequest,
  options?: RequestInit,
): Promise<LoginResponse> => {
  return customFetch<LoginResponse>(getLoginUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(loginRequest),
  });
};

export const getLoginMutationOptions = <
  TError = ErrorType<ApiError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof login>>,
    TError,
    { data: BodyType<LoginRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof login>>,
  TError,
  { data: BodyType<LoginRequest> },
  TContext
> => {
  const mutationKey = ["login"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof login>>,
    { data: BodyType<LoginRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return login(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type LoginMutationResult = NonNullable<
  Awaited<ReturnType<typeof login>>
>;
export type LoginMutationBody = BodyType<LoginRequest>;
export type LoginMutationError = ErrorType<ApiError>;

/**
 * @summary Login
 */
export const useLogin = <
  TError = ErrorType<ApiError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof login>>,
    TError,
    { data: BodyType<LoginRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof login>>,
  TError,
  { data: BodyType<LoginRequest> },
  TContext
> => {
  return useMutation(getLoginMutationOptions(options));
};

/**
 * @summary Logout
 */
export const getLogoutUrl = () => {
  return `/api/auth/logout`;
};

export const logout = async (options?: RequestInit): Promise<ApiSuccess> => {
  return customFetch<ApiSuccess>(getLogoutUrl(), {
    ...options,
    method: "POST",
  });
};

export const getLogoutMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof logout>>,
    TError,
    void,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof logout>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["logout"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof logout>>,
    void
  > = () => {
    return logout(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type LogoutMutationResult = NonNullable<
  Awaited<ReturnType<typeof logout>>
>;

export type LogoutMutationError = ErrorType<unknown>;

/**
 * @summary Logout
 */
export const useLogout = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof logout>>,
    TError,
    void,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof logout>>,
  TError,
  void,
  TContext
> => {
  return useMutation(getLogoutMutationOptions(options));
};

/**
 * @summary Refresh access token
 */
export const getRefreshTokenUrl = () => {
  return `/api/auth/refresh`;
};

export const refreshToken = async (
  refreshRequest: RefreshRequest,
  options?: RequestInit,
): Promise<RefreshResponse> => {
  return customFetch<RefreshResponse>(getRefreshTokenUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(refreshRequest),
  });
};

export const getRefreshTokenMutationOptions = <
  TError = ErrorType<ApiError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof refreshToken>>,
    TError,
    { data: BodyType<RefreshRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof refreshToken>>,
  TError,
  { data: BodyType<RefreshRequest> },
  TContext
> => {
  const mutationKey = ["refreshToken"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof refreshToken>>,
    { data: BodyType<RefreshRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return refreshToken(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RefreshTokenMutationResult = NonNullable<
  Awaited<ReturnType<typeof refreshToken>>
>;
export type RefreshTokenMutationBody = BodyType<RefreshRequest>;
export type RefreshTokenMutationError = ErrorType<ApiError>;

/**
 * @summary Refresh access token
 */
export const useRefreshToken = <
  TError = ErrorType<ApiError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof refreshToken>>,
    TError,
    { data: BodyType<RefreshRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof refreshToken>>,
  TError,
  { data: BodyType<RefreshRequest> },
  TContext
> => {
  return useMutation(getRefreshTokenMutationOptions(options));
};

/**
 * @summary Get current user
 */
export const getGetMeUrl = () => {
  return `/api/auth/me`;
};

export const getMe = async (options?: RequestInit): Promise<UserProfile> => {
  return customFetch<UserProfile>(getGetMeUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetMeQueryKey = () => {
  return [`/api/auth/me`] as const;
};

export const getGetMeQueryOptions = <
  TData = Awaited<ReturnType<typeof getMe>>,
  TError = ErrorType<ApiError>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMeQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getMe>>> = ({
    signal,
  }) => getMe({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getMe>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetMeQueryResult = NonNullable<Awaited<ReturnType<typeof getMe>>>;
export type GetMeQueryError = ErrorType<ApiError>;

/**
 * @summary Get current user
 */

export function useGetMe<
  TData = Awaited<ReturnType<typeof getMe>>,
  TError = ErrorType<ApiError>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetMeQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary List branches
 */
export const getListBranchesUrl = () => {
  return `/api/branches`;
};

export const listBranches = async (
  options?: RequestInit,
): Promise<BranchListResponse> => {
  return customFetch<BranchListResponse>(getListBranchesUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListBranchesQueryKey = () => {
  return [`/api/branches`] as const;
};

export const getListBranchesQueryOptions = <
  TData = Awaited<ReturnType<typeof listBranches>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listBranches>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListBranchesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listBranches>>> = ({
    signal,
  }) => listBranches({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listBranches>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListBranchesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listBranches>>
>;
export type ListBranchesQueryError = ErrorType<unknown>;

/**
 * @summary List branches
 */

export function useListBranches<
  TData = Awaited<ReturnType<typeof listBranches>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listBranches>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListBranchesQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create branch
 */
export const getCreateBranchUrl = () => {
  return `/api/branches`;
};

export const createBranch = async (
  createBranchRequest: CreateBranchRequest,
  options?: RequestInit,
): Promise<BranchResponse> => {
  return customFetch<BranchResponse>(getCreateBranchUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createBranchRequest),
  });
};

export const getCreateBranchMutationOptions = <
  TError = ErrorType<ApiError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createBranch>>,
    TError,
    { data: BodyType<CreateBranchRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createBranch>>,
  TError,
  { data: BodyType<CreateBranchRequest> },
  TContext
> => {
  const mutationKey = ["createBranch"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createBranch>>,
    { data: BodyType<CreateBranchRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return createBranch(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateBranchMutationResult = NonNullable<
  Awaited<ReturnType<typeof createBranch>>
>;
export type CreateBranchMutationBody = BodyType<CreateBranchRequest>;
export type CreateBranchMutationError = ErrorType<ApiError>;

/**
 * @summary Create branch
 */
export const useCreateBranch = <
  TError = ErrorType<ApiError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createBranch>>,
    TError,
    { data: BodyType<CreateBranchRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createBranch>>,
  TError,
  { data: BodyType<CreateBranchRequest> },
  TContext
> => {
  return useMutation(getCreateBranchMutationOptions(options));
};

/**
 * @summary Get branch detail
 */
export const getGetBranchUrl = (id: string) => {
  return `/api/branches/${id}`;
};

export const getBranch = async (
  id: string,
  options?: RequestInit,
): Promise<BranchResponse> => {
  return customFetch<BranchResponse>(getGetBranchUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetBranchQueryKey = (id: string) => {
  return [`/api/branches/${id}`] as const;
};

export const getGetBranchQueryOptions = <
  TData = Awaited<ReturnType<typeof getBranch>>,
  TError = ErrorType<ApiError>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBranch>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBranchQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBranch>>> = ({
    signal,
  }) => getBranch(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getBranch>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetBranchQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBranch>>
>;
export type GetBranchQueryError = ErrorType<ApiError>;

/**
 * @summary Get branch detail
 */

export function useGetBranch<
  TData = Awaited<ReturnType<typeof getBranch>>,
  TError = ErrorType<ApiError>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBranch>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetBranchQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update branch
 */
export const getUpdateBranchUrl = (id: string) => {
  return `/api/branches/${id}`;
};

export const updateBranch = async (
  id: string,
  updateBranchRequest: UpdateBranchRequest,
  options?: RequestInit,
): Promise<BranchResponse> => {
  return customFetch<BranchResponse>(getUpdateBranchUrl(id), {
    ...options,
    method: "PUT",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateBranchRequest),
  });
};

export const getUpdateBranchMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateBranch>>,
    TError,
    { id: string; data: BodyType<UpdateBranchRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateBranch>>,
  TError,
  { id: string; data: BodyType<UpdateBranchRequest> },
  TContext
> => {
  const mutationKey = ["updateBranch"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateBranch>>,
    { id: string; data: BodyType<UpdateBranchRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateBranch(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateBranchMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateBranch>>
>;
export type UpdateBranchMutationBody = BodyType<UpdateBranchRequest>;
export type UpdateBranchMutationError = ErrorType<unknown>;

/**
 * @summary Update branch
 */
export const useUpdateBranch = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateBranch>>,
    TError,
    { id: string; data: BodyType<UpdateBranchRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateBranch>>,
  TError,
  { id: string; data: BodyType<UpdateBranchRequest> },
  TContext
> => {
  return useMutation(getUpdateBranchMutationOptions(options));
};

/**
 * @summary Branch dashboard stats
 */
export const getGetBranchDashboardUrl = (id: string) => {
  return `/api/branches/${id}/dashboard`;
};

export const getBranchDashboard = async (
  id: string,
  options?: RequestInit,
): Promise<BranchDashboard> => {
  return customFetch<BranchDashboard>(getGetBranchDashboardUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetBranchDashboardQueryKey = (id: string) => {
  return [`/api/branches/${id}/dashboard`] as const;
};

export const getGetBranchDashboardQueryOptions = <
  TData = Awaited<ReturnType<typeof getBranchDashboard>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBranchDashboard>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBranchDashboardQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBranchDashboard>>
  > = ({ signal }) => getBranchDashboard(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getBranchDashboard>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetBranchDashboardQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBranchDashboard>>
>;
export type GetBranchDashboardQueryError = ErrorType<unknown>;

/**
 * @summary Branch dashboard stats
 */

export function useGetBranchDashboard<
  TData = Awaited<ReturnType<typeof getBranchDashboard>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBranchDashboard>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetBranchDashboardQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get real-time room grid snapshot
 */
export const getGetBranchRoomBoardUrl = (id: string) => {
  return `/api/branches/${id}/room-board`;
};

export const getBranchRoomBoard = async (
  id: string,
  options?: RequestInit,
): Promise<RoomBoardResponse> => {
  return customFetch<RoomBoardResponse>(getGetBranchRoomBoardUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetBranchRoomBoardQueryKey = (id: string) => {
  return [`/api/branches/${id}/room-board`] as const;
};

export const getGetBranchRoomBoardQueryOptions = <
  TData = Awaited<ReturnType<typeof getBranchRoomBoard>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBranchRoomBoard>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBranchRoomBoardQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBranchRoomBoard>>
  > = ({ signal }) => getBranchRoomBoard(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getBranchRoomBoard>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetBranchRoomBoardQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBranchRoomBoard>>
>;
export type GetBranchRoomBoardQueryError = ErrorType<unknown>;

/**
 * @summary Get real-time room grid snapshot
 */

export function useGetBranchRoomBoard<
  TData = Awaited<ReturnType<typeof getBranchRoomBoard>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBranchRoomBoard>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetBranchRoomBoardQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary List rooms
 */
export const getListRoomsUrl = (params?: ListRoomsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/rooms?${stringifiedParams}`
    : `/api/rooms`;
};

export const listRooms = async (
  params?: ListRoomsParams,
  options?: RequestInit,
): Promise<RoomListResponse> => {
  return customFetch<RoomListResponse>(getListRoomsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListRoomsQueryKey = (params?: ListRoomsParams) => {
  return [`/api/rooms`, ...(params ? [params] : [])] as const;
};

export const getListRoomsQueryOptions = <
  TData = Awaited<ReturnType<typeof listRooms>>,
  TError = ErrorType<unknown>,
>(
  params?: ListRoomsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listRooms>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListRoomsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listRooms>>> = ({
    signal,
  }) => listRooms(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listRooms>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListRoomsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listRooms>>
>;
export type ListRoomsQueryError = ErrorType<unknown>;

/**
 * @summary List rooms
 */

export function useListRooms<
  TData = Awaited<ReturnType<typeof listRooms>>,
  TError = ErrorType<unknown>,
>(
  params?: ListRoomsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listRooms>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListRoomsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create room
 */
export const getCreateRoomUrl = () => {
  return `/api/rooms`;
};

export const createRoom = async (
  createRoomRequest: CreateRoomRequest,
  options?: RequestInit,
): Promise<RoomResponse> => {
  return customFetch<RoomResponse>(getCreateRoomUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createRoomRequest),
  });
};

export const getCreateRoomMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createRoom>>,
    TError,
    { data: BodyType<CreateRoomRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createRoom>>,
  TError,
  { data: BodyType<CreateRoomRequest> },
  TContext
> => {
  const mutationKey = ["createRoom"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createRoom>>,
    { data: BodyType<CreateRoomRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return createRoom(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateRoomMutationResult = NonNullable<
  Awaited<ReturnType<typeof createRoom>>
>;
export type CreateRoomMutationBody = BodyType<CreateRoomRequest>;
export type CreateRoomMutationError = ErrorType<unknown>;

/**
 * @summary Create room
 */
export const useCreateRoom = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createRoom>>,
    TError,
    { data: BodyType<CreateRoomRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createRoom>>,
  TError,
  { data: BodyType<CreateRoomRequest> },
  TContext
> => {
  return useMutation(getCreateRoomMutationOptions(options));
};

/**
 * @summary List available rooms for a time slot
 */
export const getListAvailableRoomsUrl = (params: ListAvailableRoomsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/rooms/available?${stringifiedParams}`
    : `/api/rooms/available`;
};

export const listAvailableRooms = async (
  params: ListAvailableRoomsParams,
  options?: RequestInit,
): Promise<RoomListResponse> => {
  return customFetch<RoomListResponse>(getListAvailableRoomsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListAvailableRoomsQueryKey = (
  params?: ListAvailableRoomsParams,
) => {
  return [`/api/rooms/available`, ...(params ? [params] : [])] as const;
};

export const getListAvailableRoomsQueryOptions = <
  TData = Awaited<ReturnType<typeof listAvailableRooms>>,
  TError = ErrorType<unknown>,
>(
  params: ListAvailableRoomsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listAvailableRooms>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListAvailableRoomsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listAvailableRooms>>
  > = ({ signal }) => listAvailableRooms(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listAvailableRooms>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListAvailableRoomsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listAvailableRooms>>
>;
export type ListAvailableRoomsQueryError = ErrorType<unknown>;

/**
 * @summary List available rooms for a time slot
 */

export function useListAvailableRooms<
  TData = Awaited<ReturnType<typeof listAvailableRooms>>,
  TError = ErrorType<unknown>,
>(
  params: ListAvailableRoomsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listAvailableRooms>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListAvailableRoomsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update room
 */
export const getUpdateRoomUrl = (id: string) => {
  return `/api/rooms/${id}`;
};

export const updateRoom = async (
  id: string,
  updateRoomRequest: UpdateRoomRequest,
  options?: RequestInit,
): Promise<RoomResponse> => {
  return customFetch<RoomResponse>(getUpdateRoomUrl(id), {
    ...options,
    method: "PUT",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateRoomRequest),
  });
};

export const getUpdateRoomMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateRoom>>,
    TError,
    { id: string; data: BodyType<UpdateRoomRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateRoom>>,
  TError,
  { id: string; data: BodyType<UpdateRoomRequest> },
  TContext
> => {
  const mutationKey = ["updateRoom"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateRoom>>,
    { id: string; data: BodyType<UpdateRoomRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateRoom(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateRoomMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateRoom>>
>;
export type UpdateRoomMutationBody = BodyType<UpdateRoomRequest>;
export type UpdateRoomMutationError = ErrorType<unknown>;

/**
 * @summary Update room
 */
export const useUpdateRoom = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateRoom>>,
    TError,
    { id: string; data: BodyType<UpdateRoomRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateRoom>>,
  TError,
  { id: string; data: BodyType<UpdateRoomRequest> },
  TContext
> => {
  return useMutation(getUpdateRoomMutationOptions(options));
};

/**
 * @summary Quick room status update
 */
export const getUpdateRoomStatusUrl = (id: string) => {
  return `/api/rooms/${id}/status`;
};

export const updateRoomStatus = async (
  id: string,
  updateRoomStatusRequest: UpdateRoomStatusRequest,
  options?: RequestInit,
): Promise<RoomResponse> => {
  return customFetch<RoomResponse>(getUpdateRoomStatusUrl(id), {
    ...options,
    method: "PUT",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateRoomStatusRequest),
  });
};

export const getUpdateRoomStatusMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateRoomStatus>>,
    TError,
    { id: string; data: BodyType<UpdateRoomStatusRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateRoomStatus>>,
  TError,
  { id: string; data: BodyType<UpdateRoomStatusRequest> },
  TContext
> => {
  const mutationKey = ["updateRoomStatus"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateRoomStatus>>,
    { id: string; data: BodyType<UpdateRoomStatusRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateRoomStatus(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateRoomStatusMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateRoomStatus>>
>;
export type UpdateRoomStatusMutationBody = BodyType<UpdateRoomStatusRequest>;
export type UpdateRoomStatusMutationError = ErrorType<unknown>;

/**
 * @summary Quick room status update
 */
export const useUpdateRoomStatus = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateRoomStatus>>,
    TError,
    { id: string; data: BodyType<UpdateRoomStatusRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateRoomStatus>>,
  TError,
  { id: string; data: BodyType<UpdateRoomStatusRequest> },
  TContext
> => {
  return useMutation(getUpdateRoomStatusMutationOptions(options));
};

/**
 * @summary List product groups
 */
export const getListProductGroupsUrl = () => {
  return `/api/products/groups`;
};

export const listProductGroups = async (
  options?: RequestInit,
): Promise<ProductGroupListResponse> => {
  return customFetch<ProductGroupListResponse>(getListProductGroupsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListProductGroupsQueryKey = () => {
  return [`/api/products/groups`] as const;
};

export const getListProductGroupsQueryOptions = <
  TData = Awaited<ReturnType<typeof listProductGroups>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listProductGroups>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListProductGroupsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listProductGroups>>
  > = ({ signal }) => listProductGroups({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listProductGroups>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListProductGroupsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listProductGroups>>
>;
export type ListProductGroupsQueryError = ErrorType<unknown>;

/**
 * @summary List product groups
 */

export function useListProductGroups<
  TData = Awaited<ReturnType<typeof listProductGroups>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listProductGroups>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListProductGroupsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create product group
 */
export const getCreateProductGroupUrl = () => {
  return `/api/products/groups`;
};

export const createProductGroup = async (
  createProductGroupRequest: CreateProductGroupRequest,
  options?: RequestInit,
): Promise<ProductGroupResponse> => {
  return customFetch<ProductGroupResponse>(getCreateProductGroupUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createProductGroupRequest),
  });
};

export const getCreateProductGroupMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createProductGroup>>,
    TError,
    { data: BodyType<CreateProductGroupRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createProductGroup>>,
  TError,
  { data: BodyType<CreateProductGroupRequest> },
  TContext
> => {
  const mutationKey = ["createProductGroup"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createProductGroup>>,
    { data: BodyType<CreateProductGroupRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return createProductGroup(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateProductGroupMutationResult = NonNullable<
  Awaited<ReturnType<typeof createProductGroup>>
>;
export type CreateProductGroupMutationBody =
  BodyType<CreateProductGroupRequest>;
export type CreateProductGroupMutationError = ErrorType<unknown>;

/**
 * @summary Create product group
 */
export const useCreateProductGroup = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createProductGroup>>,
    TError,
    { data: BodyType<CreateProductGroupRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createProductGroup>>,
  TError,
  { data: BodyType<CreateProductGroupRequest> },
  TContext
> => {
  return useMutation(getCreateProductGroupMutationOptions(options));
};

/**
 * @summary List product types
 */
export const getListProductTypesUrl = (params?: ListProductTypesParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/products/types?${stringifiedParams}`
    : `/api/products/types`;
};

export const listProductTypes = async (
  params?: ListProductTypesParams,
  options?: RequestInit,
): Promise<ProductTypeListResponse> => {
  return customFetch<ProductTypeListResponse>(getListProductTypesUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListProductTypesQueryKey = (
  params?: ListProductTypesParams,
) => {
  return [`/api/products/types`, ...(params ? [params] : [])] as const;
};

export const getListProductTypesQueryOptions = <
  TData = Awaited<ReturnType<typeof listProductTypes>>,
  TError = ErrorType<unknown>,
>(
  params?: ListProductTypesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listProductTypes>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListProductTypesQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listProductTypes>>
  > = ({ signal }) => listProductTypes(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listProductTypes>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListProductTypesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listProductTypes>>
>;
export type ListProductTypesQueryError = ErrorType<unknown>;

/**
 * @summary List product types
 */

export function useListProductTypes<
  TData = Awaited<ReturnType<typeof listProductTypes>>,
  TError = ErrorType<unknown>,
>(
  params?: ListProductTypesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listProductTypes>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListProductTypesQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create product type
 */
export const getCreateProductTypeUrl = () => {
  return `/api/products/types`;
};

export const createProductType = async (
  createProductTypeRequest: CreateProductTypeRequest,
  options?: RequestInit,
): Promise<ProductTypeResponse> => {
  return customFetch<ProductTypeResponse>(getCreateProductTypeUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createProductTypeRequest),
  });
};

export const getCreateProductTypeMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createProductType>>,
    TError,
    { data: BodyType<CreateProductTypeRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createProductType>>,
  TError,
  { data: BodyType<CreateProductTypeRequest> },
  TContext
> => {
  const mutationKey = ["createProductType"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createProductType>>,
    { data: BodyType<CreateProductTypeRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return createProductType(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateProductTypeMutationResult = NonNullable<
  Awaited<ReturnType<typeof createProductType>>
>;
export type CreateProductTypeMutationBody = BodyType<CreateProductTypeRequest>;
export type CreateProductTypeMutationError = ErrorType<unknown>;

/**
 * @summary Create product type
 */
export const useCreateProductType = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createProductType>>,
    TError,
    { data: BodyType<CreateProductTypeRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createProductType>>,
  TError,
  { data: BodyType<CreateProductTypeRequest> },
  TContext
> => {
  return useMutation(getCreateProductTypeMutationOptions(options));
};

/**
 * @summary List products
 */
export const getListProductsUrl = (params?: ListProductsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/products?${stringifiedParams}`
    : `/api/products`;
};

export const listProducts = async (
  params?: ListProductsParams,
  options?: RequestInit,
): Promise<ProductListResponse> => {
  return customFetch<ProductListResponse>(getListProductsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListProductsQueryKey = (params?: ListProductsParams) => {
  return [`/api/products`, ...(params ? [params] : [])] as const;
};

export const getListProductsQueryOptions = <
  TData = Awaited<ReturnType<typeof listProducts>>,
  TError = ErrorType<unknown>,
>(
  params?: ListProductsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listProducts>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListProductsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listProducts>>> = ({
    signal,
  }) => listProducts(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listProducts>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListProductsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listProducts>>
>;
export type ListProductsQueryError = ErrorType<unknown>;

/**
 * @summary List products
 */

export function useListProducts<
  TData = Awaited<ReturnType<typeof listProducts>>,
  TError = ErrorType<unknown>,
>(
  params?: ListProductsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listProducts>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListProductsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create product
 */
export const getCreateProductUrl = () => {
  return `/api/products`;
};

export const createProduct = async (
  createProductRequest: CreateProductRequest,
  options?: RequestInit,
): Promise<ProductResponse> => {
  return customFetch<ProductResponse>(getCreateProductUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createProductRequest),
  });
};

export const getCreateProductMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createProduct>>,
    TError,
    { data: BodyType<CreateProductRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createProduct>>,
  TError,
  { data: BodyType<CreateProductRequest> },
  TContext
> => {
  const mutationKey = ["createProduct"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createProduct>>,
    { data: BodyType<CreateProductRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return createProduct(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateProductMutationResult = NonNullable<
  Awaited<ReturnType<typeof createProduct>>
>;
export type CreateProductMutationBody = BodyType<CreateProductRequest>;
export type CreateProductMutationError = ErrorType<unknown>;

/**
 * @summary Create product
 */
export const useCreateProduct = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createProduct>>,
    TError,
    { data: BodyType<CreateProductRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createProduct>>,
  TError,
  { data: BodyType<CreateProductRequest> },
  TContext
> => {
  return useMutation(getCreateProductMutationOptions(options));
};

/**
 * @summary Update product
 */
export const getUpdateProductUrl = (id: string) => {
  return `/api/products/${id}`;
};

export const updateProduct = async (
  id: string,
  updateProductRequest: UpdateProductRequest,
  options?: RequestInit,
): Promise<ProductResponse> => {
  return customFetch<ProductResponse>(getUpdateProductUrl(id), {
    ...options,
    method: "PUT",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateProductRequest),
  });
};

export const getUpdateProductMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateProduct>>,
    TError,
    { id: string; data: BodyType<UpdateProductRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateProduct>>,
  TError,
  { id: string; data: BodyType<UpdateProductRequest> },
  TContext
> => {
  const mutationKey = ["updateProduct"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateProduct>>,
    { id: string; data: BodyType<UpdateProductRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateProduct(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateProductMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateProduct>>
>;
export type UpdateProductMutationBody = BodyType<UpdateProductRequest>;
export type UpdateProductMutationError = ErrorType<unknown>;

/**
 * @summary Update product
 */
export const useUpdateProduct = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateProduct>>,
    TError,
    { id: string; data: BodyType<UpdateProductRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateProduct>>,
  TError,
  { id: string; data: BodyType<UpdateProductRequest> },
  TContext
> => {
  return useMutation(getUpdateProductMutationOptions(options));
};

/**
 * @summary Toggle product active status
 */
export const getToggleProductUrl = (id: string) => {
  return `/api/products/${id}/toggle`;
};

export const toggleProduct = async (
  id: string,
  options?: RequestInit,
): Promise<ProductResponse> => {
  return customFetch<ProductResponse>(getToggleProductUrl(id), {
    ...options,
    method: "PUT",
  });
};

export const getToggleProductMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof toggleProduct>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof toggleProduct>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["toggleProduct"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof toggleProduct>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return toggleProduct(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ToggleProductMutationResult = NonNullable<
  Awaited<ReturnType<typeof toggleProduct>>
>;

export type ToggleProductMutationError = ErrorType<unknown>;

/**
 * @summary Toggle product active status
 */
export const useToggleProduct = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof toggleProduct>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof toggleProduct>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(getToggleProductMutationOptions(options));
};

/**
 * @summary List reservations
 */
export const getListReservationsUrl = (params?: ListReservationsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/reservations?${stringifiedParams}`
    : `/api/reservations`;
};

export const listReservations = async (
  params?: ListReservationsParams,
  options?: RequestInit,
): Promise<ReservationListResponse> => {
  return customFetch<ReservationListResponse>(getListReservationsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListReservationsQueryKey = (
  params?: ListReservationsParams,
) => {
  return [`/api/reservations`, ...(params ? [params] : [])] as const;
};

export const getListReservationsQueryOptions = <
  TData = Awaited<ReturnType<typeof listReservations>>,
  TError = ErrorType<unknown>,
>(
  params?: ListReservationsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listReservations>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListReservationsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listReservations>>
  > = ({ signal }) => listReservations(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listReservations>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListReservationsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listReservations>>
>;
export type ListReservationsQueryError = ErrorType<unknown>;

/**
 * @summary List reservations
 */

export function useListReservations<
  TData = Awaited<ReturnType<typeof listReservations>>,
  TError = ErrorType<unknown>,
>(
  params?: ListReservationsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listReservations>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListReservationsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create reservation
 */
export const getCreateReservationUrl = () => {
  return `/api/reservations`;
};

export const createReservation = async (
  createReservationRequest: CreateReservationRequest,
  options?: RequestInit,
): Promise<ReservationResponse> => {
  return customFetch<ReservationResponse>(getCreateReservationUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createReservationRequest),
  });
};

export const getCreateReservationMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createReservation>>,
    TError,
    { data: BodyType<CreateReservationRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createReservation>>,
  TError,
  { data: BodyType<CreateReservationRequest> },
  TContext
> => {
  const mutationKey = ["createReservation"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createReservation>>,
    { data: BodyType<CreateReservationRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return createReservation(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateReservationMutationResult = NonNullable<
  Awaited<ReturnType<typeof createReservation>>
>;
export type CreateReservationMutationBody = BodyType<CreateReservationRequest>;
export type CreateReservationMutationError = ErrorType<unknown>;

/**
 * @summary Create reservation
 */
export const useCreateReservation = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createReservation>>,
    TError,
    { data: BodyType<CreateReservationRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createReservation>>,
  TError,
  { data: BodyType<CreateReservationRequest> },
  TContext
> => {
  return useMutation(getCreateReservationMutationOptions(options));
};

/**
 * @summary Check room availability for a date
 */
export const getGetReservationAvailabilityUrl = (
  params: GetReservationAvailabilityParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/reservations/availability?${stringifiedParams}`
    : `/api/reservations/availability`;
};

export const getReservationAvailability = async (
  params: GetReservationAvailabilityParams,
  options?: RequestInit,
): Promise<RoomListResponse> => {
  return customFetch<RoomListResponse>(
    getGetReservationAvailabilityUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetReservationAvailabilityQueryKey = (
  params?: GetReservationAvailabilityParams,
) => {
  return [
    `/api/reservations/availability`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetReservationAvailabilityQueryOptions = <
  TData = Awaited<ReturnType<typeof getReservationAvailability>>,
  TError = ErrorType<unknown>,
>(
  params: GetReservationAvailabilityParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getReservationAvailability>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetReservationAvailabilityQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getReservationAvailability>>
  > = ({ signal }) =>
    getReservationAvailability(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getReservationAvailability>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetReservationAvailabilityQueryResult = NonNullable<
  Awaited<ReturnType<typeof getReservationAvailability>>
>;
export type GetReservationAvailabilityQueryError = ErrorType<unknown>;

/**
 * @summary Check room availability for a date
 */

export function useGetReservationAvailability<
  TData = Awaited<ReturnType<typeof getReservationAvailability>>,
  TError = ErrorType<unknown>,
>(
  params: GetReservationAvailabilityParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getReservationAvailability>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetReservationAvailabilityQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get reservation detail
 */
export const getGetReservationUrl = (id: string) => {
  return `/api/reservations/${id}`;
};

export const getReservation = async (
  id: string,
  options?: RequestInit,
): Promise<ReservationResponse> => {
  return customFetch<ReservationResponse>(getGetReservationUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetReservationQueryKey = (id: string) => {
  return [`/api/reservations/${id}`] as const;
};

export const getGetReservationQueryOptions = <
  TData = Awaited<ReturnType<typeof getReservation>>,
  TError = ErrorType<ApiError>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getReservation>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetReservationQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getReservation>>> = ({
    signal,
  }) => getReservation(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getReservation>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetReservationQueryResult = NonNullable<
  Awaited<ReturnType<typeof getReservation>>
>;
export type GetReservationQueryError = ErrorType<ApiError>;

/**
 * @summary Get reservation detail
 */

export function useGetReservation<
  TData = Awaited<ReturnType<typeof getReservation>>,
  TError = ErrorType<ApiError>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getReservation>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetReservationQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Confirm reservation
 */
export const getConfirmReservationUrl = (id: string) => {
  return `/api/reservations/${id}/confirm`;
};

export const confirmReservation = async (
  id: string,
  options?: RequestInit,
): Promise<ReservationResponse> => {
  return customFetch<ReservationResponse>(getConfirmReservationUrl(id), {
    ...options,
    method: "PUT",
  });
};

export const getConfirmReservationMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof confirmReservation>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof confirmReservation>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["confirmReservation"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof confirmReservation>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return confirmReservation(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ConfirmReservationMutationResult = NonNullable<
  Awaited<ReturnType<typeof confirmReservation>>
>;

export type ConfirmReservationMutationError = ErrorType<unknown>;

/**
 * @summary Confirm reservation
 */
export const useConfirmReservation = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof confirmReservation>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof confirmReservation>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(getConfirmReservationMutationOptions(options));
};

/**
 * @summary Check-in reservation
 */
export const getCheckInReservationUrl = (id: string) => {
  return `/api/reservations/${id}/check-in`;
};

export const checkInReservation = async (
  id: string,
  options?: RequestInit,
): Promise<ReservationResponse> => {
  return customFetch<ReservationResponse>(getCheckInReservationUrl(id), {
    ...options,
    method: "PUT",
  });
};

export const getCheckInReservationMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof checkInReservation>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof checkInReservation>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["checkInReservation"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof checkInReservation>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return checkInReservation(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CheckInReservationMutationResult = NonNullable<
  Awaited<ReturnType<typeof checkInReservation>>
>;

export type CheckInReservationMutationError = ErrorType<unknown>;

/**
 * @summary Check-in reservation
 */
export const useCheckInReservation = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof checkInReservation>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof checkInReservation>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(getCheckInReservationMutationOptions(options));
};

/**
 * @summary Extend reservation stay
 */
export const getExtendReservationUrl = (id: string) => {
  return `/api/reservations/${id}/extend`;
};

export const extendReservation = async (
  id: string,
  extendReservationRequest: ExtendReservationRequest,
  options?: RequestInit,
): Promise<ReservationResponse> => {
  return customFetch<ReservationResponse>(getExtendReservationUrl(id), {
    ...options,
    method: "PUT",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(extendReservationRequest),
  });
};

export const getExtendReservationMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof extendReservation>>,
    TError,
    { id: string; data: BodyType<ExtendReservationRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof extendReservation>>,
  TError,
  { id: string; data: BodyType<ExtendReservationRequest> },
  TContext
> => {
  const mutationKey = ["extendReservation"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof extendReservation>>,
    { id: string; data: BodyType<ExtendReservationRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return extendReservation(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ExtendReservationMutationResult = NonNullable<
  Awaited<ReturnType<typeof extendReservation>>
>;
export type ExtendReservationMutationBody = BodyType<ExtendReservationRequest>;
export type ExtendReservationMutationError = ErrorType<unknown>;

/**
 * @summary Extend reservation stay
 */
export const useExtendReservation = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof extendReservation>>,
    TError,
    { id: string; data: BodyType<ExtendReservationRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof extendReservation>>,
  TError,
  { id: string; data: BodyType<ExtendReservationRequest> },
  TContext
> => {
  return useMutation(getExtendReservationMutationOptions(options));
};

/**
 * @summary Check-out reservation
 */
export const getCheckOutReservationUrl = (id: string) => {
  return `/api/reservations/${id}/check-out`;
};

export const checkOutReservation = async (
  id: string,
  options?: RequestInit,
): Promise<ReservationResponse> => {
  return customFetch<ReservationResponse>(getCheckOutReservationUrl(id), {
    ...options,
    method: "PUT",
  });
};

export const getCheckOutReservationMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof checkOutReservation>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof checkOutReservation>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["checkOutReservation"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof checkOutReservation>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return checkOutReservation(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CheckOutReservationMutationResult = NonNullable<
  Awaited<ReturnType<typeof checkOutReservation>>
>;

export type CheckOutReservationMutationError = ErrorType<unknown>;

/**
 * @summary Check-out reservation
 */
export const useCheckOutReservation = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof checkOutReservation>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof checkOutReservation>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(getCheckOutReservationMutationOptions(options));
};

/**
 * @summary Cancel reservation
 */
export const getCancelReservationUrl = (id: string) => {
  return `/api/reservations/${id}/cancel`;
};

export const cancelReservation = async (
  id: string,
  cancelReservationRequest: CancelReservationRequest,
  options?: RequestInit,
): Promise<ReservationResponse> => {
  return customFetch<ReservationResponse>(getCancelReservationUrl(id), {
    ...options,
    method: "PUT",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(cancelReservationRequest),
  });
};

export const getCancelReservationMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof cancelReservation>>,
    TError,
    { id: string; data: BodyType<CancelReservationRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof cancelReservation>>,
  TError,
  { id: string; data: BodyType<CancelReservationRequest> },
  TContext
> => {
  const mutationKey = ["cancelReservation"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof cancelReservation>>,
    { id: string; data: BodyType<CancelReservationRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return cancelReservation(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CancelReservationMutationResult = NonNullable<
  Awaited<ReturnType<typeof cancelReservation>>
>;
export type CancelReservationMutationBody = BodyType<CancelReservationRequest>;
export type CancelReservationMutationError = ErrorType<unknown>;

/**
 * @summary Cancel reservation
 */
export const useCancelReservation = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof cancelReservation>>,
    TError,
    { id: string; data: BodyType<CancelReservationRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof cancelReservation>>,
  TError,
  { id: string; data: BodyType<CancelReservationRequest> },
  TContext
> => {
  return useMutation(getCancelReservationMutationOptions(options));
};

/**
 * @summary List orders
 */
export const getListOrdersUrl = (params?: ListOrdersParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/orders?${stringifiedParams}`
    : `/api/orders`;
};

export const listOrders = async (
  params?: ListOrdersParams,
  options?: RequestInit,
): Promise<OrderListResponse> => {
  return customFetch<OrderListResponse>(getListOrdersUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListOrdersQueryKey = (params?: ListOrdersParams) => {
  return [`/api/orders`, ...(params ? [params] : [])] as const;
};

export const getListOrdersQueryOptions = <
  TData = Awaited<ReturnType<typeof listOrders>>,
  TError = ErrorType<unknown>,
>(
  params?: ListOrdersParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listOrders>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListOrdersQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listOrders>>> = ({
    signal,
  }) => listOrders(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listOrders>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListOrdersQueryResult = NonNullable<
  Awaited<ReturnType<typeof listOrders>>
>;
export type ListOrdersQueryError = ErrorType<unknown>;

/**
 * @summary List orders
 */

export function useListOrders<
  TData = Awaited<ReturnType<typeof listOrders>>,
  TError = ErrorType<unknown>,
>(
  params?: ListOrdersParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listOrders>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListOrdersQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create order
 */
export const getCreateOrderUrl = () => {
  return `/api/orders`;
};

export const createOrder = async (
  createOrderRequest: CreateOrderRequest,
  options?: RequestInit,
): Promise<OrderResponse> => {
  return customFetch<OrderResponse>(getCreateOrderUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createOrderRequest),
  });
};

export const getCreateOrderMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOrder>>,
    TError,
    { data: BodyType<CreateOrderRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createOrder>>,
  TError,
  { data: BodyType<CreateOrderRequest> },
  TContext
> => {
  const mutationKey = ["createOrder"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createOrder>>,
    { data: BodyType<CreateOrderRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return createOrder(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateOrderMutationResult = NonNullable<
  Awaited<ReturnType<typeof createOrder>>
>;
export type CreateOrderMutationBody = BodyType<CreateOrderRequest>;
export type CreateOrderMutationError = ErrorType<unknown>;

/**
 * @summary Create order
 */
export const useCreateOrder = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOrder>>,
    TError,
    { data: BodyType<CreateOrderRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createOrder>>,
  TError,
  { data: BodyType<CreateOrderRequest> },
  TContext
> => {
  return useMutation(getCreateOrderMutationOptions(options));
};

/**
 * @summary Add item to order
 */
export const getAddOrderItemUrl = (id: string) => {
  return `/api/orders/${id}/items`;
};

export const addOrderItem = async (
  id: string,
  addOrderItemRequest: AddOrderItemRequest,
  options?: RequestInit,
): Promise<OrderResponse> => {
  return customFetch<OrderResponse>(getAddOrderItemUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(addOrderItemRequest),
  });
};

export const getAddOrderItemMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addOrderItem>>,
    TError,
    { id: string; data: BodyType<AddOrderItemRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof addOrderItem>>,
  TError,
  { id: string; data: BodyType<AddOrderItemRequest> },
  TContext
> => {
  const mutationKey = ["addOrderItem"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof addOrderItem>>,
    { id: string; data: BodyType<AddOrderItemRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return addOrderItem(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AddOrderItemMutationResult = NonNullable<
  Awaited<ReturnType<typeof addOrderItem>>
>;
export type AddOrderItemMutationBody = BodyType<AddOrderItemRequest>;
export type AddOrderItemMutationError = ErrorType<unknown>;

/**
 * @summary Add item to order
 */
export const useAddOrderItem = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addOrderItem>>,
    TError,
    { id: string; data: BodyType<AddOrderItemRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof addOrderItem>>,
  TError,
  { id: string; data: BodyType<AddOrderItemRequest> },
  TContext
> => {
  return useMutation(getAddOrderItemMutationOptions(options));
};

/**
 * @summary Remove item from order
 */
export const getRemoveOrderItemUrl = (id: string, itemId: string) => {
  return `/api/orders/${id}/items/${itemId}`;
};

export const removeOrderItem = async (
  id: string,
  itemId: string,
  options?: RequestInit,
): Promise<OrderResponse> => {
  return customFetch<OrderResponse>(getRemoveOrderItemUrl(id, itemId), {
    ...options,
    method: "DELETE",
  });
};

export const getRemoveOrderItemMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeOrderItem>>,
    TError,
    { id: string; itemId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof removeOrderItem>>,
  TError,
  { id: string; itemId: string },
  TContext
> => {
  const mutationKey = ["removeOrderItem"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof removeOrderItem>>,
    { id: string; itemId: string }
  > = (props) => {
    const { id, itemId } = props ?? {};

    return removeOrderItem(id, itemId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemoveOrderItemMutationResult = NonNullable<
  Awaited<ReturnType<typeof removeOrderItem>>
>;

export type RemoveOrderItemMutationError = ErrorType<unknown>;

/**
 * @summary Remove item from order
 */
export const useRemoveOrderItem = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeOrderItem>>,
    TError,
    { id: string; itemId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof removeOrderItem>>,
  TError,
  { id: string; itemId: string },
  TContext
> => {
  return useMutation(getRemoveOrderItemMutationOptions(options));
};

/**
 * @summary Apply discount to order
 */
export const getApplyOrderDiscountUrl = (id: string) => {
  return `/api/orders/${id}/discount`;
};

export const applyOrderDiscount = async (
  id: string,
  applyDiscountRequest: ApplyDiscountRequest,
  options?: RequestInit,
): Promise<OrderResponse> => {
  return customFetch<OrderResponse>(getApplyOrderDiscountUrl(id), {
    ...options,
    method: "PUT",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(applyDiscountRequest),
  });
};

export const getApplyOrderDiscountMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof applyOrderDiscount>>,
    TError,
    { id: string; data: BodyType<ApplyDiscountRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof applyOrderDiscount>>,
  TError,
  { id: string; data: BodyType<ApplyDiscountRequest> },
  TContext
> => {
  const mutationKey = ["applyOrderDiscount"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof applyOrderDiscount>>,
    { id: string; data: BodyType<ApplyDiscountRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return applyOrderDiscount(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ApplyOrderDiscountMutationResult = NonNullable<
  Awaited<ReturnType<typeof applyOrderDiscount>>
>;
export type ApplyOrderDiscountMutationBody = BodyType<ApplyDiscountRequest>;
export type ApplyOrderDiscountMutationError = ErrorType<unknown>;

/**
 * @summary Apply discount to order
 */
export const useApplyOrderDiscount = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof applyOrderDiscount>>,
    TError,
    { id: string; data: BodyType<ApplyDiscountRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof applyOrderDiscount>>,
  TError,
  { id: string; data: BodyType<ApplyDiscountRequest> },
  TContext
> => {
  return useMutation(getApplyOrderDiscountMutationOptions(options));
};

/**
 * @summary Finalize order (lock and calculate totals)
 */
export const getFinalizeOrderUrl = (id: string) => {
  return `/api/orders/${id}/finalize`;
};

export const finalizeOrder = async (
  id: string,
  options?: RequestInit,
): Promise<OrderResponse> => {
  return customFetch<OrderResponse>(getFinalizeOrderUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getFinalizeOrderMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof finalizeOrder>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof finalizeOrder>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["finalizeOrder"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof finalizeOrder>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return finalizeOrder(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type FinalizeOrderMutationResult = NonNullable<
  Awaited<ReturnType<typeof finalizeOrder>>
>;

export type FinalizeOrderMutationError = ErrorType<unknown>;

/**
 * @summary Finalize order (lock and calculate totals)
 */
export const useFinalizeOrder = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof finalizeOrder>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof finalizeOrder>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(getFinalizeOrderMutationOptions(options));
};

/**
 * @summary Get invoice HTML for order
 */
export const getGetOrderInvoiceUrl = (
  id: string,
  params?: GetOrderInvoiceParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/orders/${id}/invoice?${stringifiedParams}`
    : `/api/orders/${id}/invoice`;
};

export const getOrderInvoice = async (
  id: string,
  params?: GetOrderInvoiceParams,
  options?: RequestInit,
): Promise<string> => {
  return customFetch<string>(getGetOrderInvoiceUrl(id, params), {
    ...options,
    method: "GET",
  });
};

export const getGetOrderInvoiceQueryKey = (
  id: string,
  params?: GetOrderInvoiceParams,
) => {
  return [`/api/orders/${id}/invoice`, ...(params ? [params] : [])] as const;
};

export const getGetOrderInvoiceQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrderInvoice>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetOrderInvoiceParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOrderInvoice>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetOrderInvoiceQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrderInvoice>>> = ({
    signal,
  }) => getOrderInvoice(id, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrderInvoice>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetOrderInvoiceQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOrderInvoice>>
>;
export type GetOrderInvoiceQueryError = ErrorType<unknown>;

/**
 * @summary Get invoice HTML for order
 */

export function useGetOrderInvoice<
  TData = Awaited<ReturnType<typeof getOrderInvoice>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetOrderInvoiceParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOrderInvoice>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetOrderInvoiceQueryOptions(id, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create receipt after payment
 */
export const getCreateReceiptUrl = (id: string) => {
  return `/api/orders/${id}/receipt`;
};

export const createReceipt = async (
  id: string,
  createReceiptRequest: CreateReceiptRequest,
  options?: RequestInit,
): Promise<ReceiptResponse> => {
  return customFetch<ReceiptResponse>(getCreateReceiptUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createReceiptRequest),
  });
};

export const getCreateReceiptMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createReceipt>>,
    TError,
    { id: string; data: BodyType<CreateReceiptRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createReceipt>>,
  TError,
  { id: string; data: BodyType<CreateReceiptRequest> },
  TContext
> => {
  const mutationKey = ["createReceipt"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createReceipt>>,
    { id: string; data: BodyType<CreateReceiptRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return createReceipt(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateReceiptMutationResult = NonNullable<
  Awaited<ReturnType<typeof createReceipt>>
>;
export type CreateReceiptMutationBody = BodyType<CreateReceiptRequest>;
export type CreateReceiptMutationError = ErrorType<unknown>;

/**
 * @summary Create receipt after payment
 */
export const useCreateReceipt = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createReceipt>>,
    TError,
    { id: string; data: BodyType<CreateReceiptRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createReceipt>>,
  TError,
  { id: string; data: BodyType<CreateReceiptRequest> },
  TContext
> => {
  return useMutation(getCreateReceiptMutationOptions(options));
};

/**
 * @summary Get latest receipt for order
 */
export const getGetLatestReceiptUrl = (id: string) => {
  return `/api/orders/${id}/receipt/latest`;
};

export const getLatestReceipt = async (
  id: string,
  options?: RequestInit,
): Promise<ReceiptResponse> => {
  return customFetch<ReceiptResponse>(getGetLatestReceiptUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetLatestReceiptQueryKey = (id: string) => {
  return [`/api/orders/${id}/receipt/latest`] as const;
};

export const getGetLatestReceiptQueryOptions = <
  TData = Awaited<ReturnType<typeof getLatestReceipt>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getLatestReceipt>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLatestReceiptQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getLatestReceipt>>
  > = ({ signal }) => getLatestReceipt(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getLatestReceipt>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetLatestReceiptQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLatestReceipt>>
>;
export type GetLatestReceiptQueryError = ErrorType<unknown>;

/**
 * @summary Get latest receipt for order
 */

export function useGetLatestReceipt<
  TData = Awaited<ReturnType<typeof getLatestReceipt>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getLatestReceipt>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetLatestReceiptQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get receipt HTML
 */
export const getGetReceiptUrl = (id: string, params?: GetReceiptParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/receipts/${id}?${stringifiedParams}`
    : `/api/receipts/${id}`;
};

export const getReceipt = async (
  id: string,
  params?: GetReceiptParams,
  options?: RequestInit,
): Promise<string> => {
  return customFetch<string>(getGetReceiptUrl(id, params), {
    ...options,
    method: "GET",
  });
};

export const getGetReceiptQueryKey = (
  id: string,
  params?: GetReceiptParams,
) => {
  return [`/api/receipts/${id}`, ...(params ? [params] : [])] as const;
};

export const getGetReceiptQueryOptions = <
  TData = Awaited<ReturnType<typeof getReceipt>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetReceiptParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getReceipt>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetReceiptQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getReceipt>>> = ({
    signal,
  }) => getReceipt(id, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getReceipt>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetReceiptQueryResult = NonNullable<
  Awaited<ReturnType<typeof getReceipt>>
>;
export type GetReceiptQueryError = ErrorType<unknown>;

/**
 * @summary Get receipt HTML
 */

export function useGetReceipt<
  TData = Awaited<ReturnType<typeof getReceipt>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetReceiptParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getReceipt>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetReceiptQueryOptions(id, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Record receipt print event
 */
export const getRecordReceiptPrintUrl = (id: string) => {
  return `/api/receipts/${id}/printed`;
};

export const recordReceiptPrint = async (
  id: string,
  options?: RequestInit,
): Promise<ApiSuccess> => {
  return customFetch<ApiSuccess>(getRecordReceiptPrintUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getRecordReceiptPrintMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof recordReceiptPrint>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof recordReceiptPrint>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["recordReceiptPrint"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof recordReceiptPrint>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return recordReceiptPrint(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RecordReceiptPrintMutationResult = NonNullable<
  Awaited<ReturnType<typeof recordReceiptPrint>>
>;

export type RecordReceiptPrintMutationError = ErrorType<unknown>;

/**
 * @summary Record receipt print event
 */
export const useRecordReceiptPrint = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof recordReceiptPrint>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof recordReceiptPrint>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(getRecordReceiptPrintMutationOptions(options));
};

/**
 * @summary List available hostesses for a time slot
 */
export const getListAvailableHostessesUrl = (
  params: ListAvailableHostessesParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/staff/hostesses/available?${stringifiedParams}`
    : `/api/staff/hostesses/available`;
};

export const listAvailableHostesses = async (
  params: ListAvailableHostessesParams,
  options?: RequestInit,
): Promise<StaffAvailabilityResponse> => {
  return customFetch<StaffAvailabilityResponse>(
    getListAvailableHostessesUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListAvailableHostessesQueryKey = (
  params?: ListAvailableHostessesParams,
) => {
  return [
    `/api/staff/hostesses/available`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListAvailableHostessesQueryOptions = <
  TData = Awaited<ReturnType<typeof listAvailableHostesses>>,
  TError = ErrorType<unknown>,
>(
  params: ListAvailableHostessesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listAvailableHostesses>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListAvailableHostessesQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listAvailableHostesses>>
  > = ({ signal }) =>
    listAvailableHostesses(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listAvailableHostesses>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListAvailableHostessesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listAvailableHostesses>>
>;
export type ListAvailableHostessesQueryError = ErrorType<unknown>;

/**
 * @summary List available hostesses for a time slot
 */

export function useListAvailableHostesses<
  TData = Awaited<ReturnType<typeof listAvailableHostesses>>,
  TError = ErrorType<unknown>,
>(
  params: ListAvailableHostessesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listAvailableHostesses>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListAvailableHostessesQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary List available drivers for a time slot
 */
export const getListAvailableDriversUrl = (
  params: ListAvailableDriversParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/staff/drivers/available?${stringifiedParams}`
    : `/api/staff/drivers/available`;
};

export const listAvailableDrivers = async (
  params: ListAvailableDriversParams,
  options?: RequestInit,
): Promise<StaffAvailabilityResponse> => {
  return customFetch<StaffAvailabilityResponse>(
    getListAvailableDriversUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListAvailableDriversQueryKey = (
  params?: ListAvailableDriversParams,
) => {
  return [`/api/staff/drivers/available`, ...(params ? [params] : [])] as const;
};

export const getListAvailableDriversQueryOptions = <
  TData = Awaited<ReturnType<typeof listAvailableDrivers>>,
  TError = ErrorType<unknown>,
>(
  params: ListAvailableDriversParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listAvailableDrivers>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListAvailableDriversQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listAvailableDrivers>>
  > = ({ signal }) =>
    listAvailableDrivers(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listAvailableDrivers>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListAvailableDriversQueryResult = NonNullable<
  Awaited<ReturnType<typeof listAvailableDrivers>>
>;
export type ListAvailableDriversQueryError = ErrorType<unknown>;

/**
 * @summary List available drivers for a time slot
 */

export function useListAvailableDrivers<
  TData = Awaited<ReturnType<typeof listAvailableDrivers>>,
  TError = ErrorType<unknown>,
>(
  params: ListAvailableDriversParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listAvailableDrivers>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListAvailableDriversQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}
